<template>
    <AppLayout title="Daftar Topik Penjelasan">
        <Toast />
        <div class="py-4">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg p-3">
                    <div class="mb-3">
                        <h2 class="text-lg font-semibold">
                            Topik Penjelasan: {{ dokumenPengadaan.nama_paket }}
                        </h2>
                    </div>

                    <!-- Compact Subject List - 2 Column Grid -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div v-for="subject in localSubjects" :key="subject.id" :data-subject-id="subject.id" class="border rounded-lg p-2 h-full flex flex-col">
                            <!-- Subject Header -->
                            <div class="flex justify-between items-center mb-1 px-2 pt-2">
                                <h3 class="font-medium text-base truncate max-w-[70%]" :title="decodeHtml(subject.subject)" v-html="decodeHtml(subject.subject)"></h3>
                                <div class="text-xs text-gray-500">{{ formatDate(subject.created_at) }}</div>
                            </div>
                            
                            <!-- Description -->
                            <p class="text-xs text-gray-600 mb-1 px-2 line-clamp-2" :title="decodeHtml(subject.description)" v-html="decodeHtml(subject.description)"></p>
                            <div class="text-xs text-gray-500 mb-2 px-2">Dibuat: {{ subject.creator_name }}</div>
                            
                            <!-- Attachments -->
                            <div v-if="subject.attachments && subject.attachments.length > 0" class="px-2 mb-2">
                                <div class="text-xs text-gray-600 mb-1">📎 Lampiran:</div>
                                <div class="flex flex-wrap gap-1">
                                    <a v-for="attachment in subject.attachments" 
                                       :key="attachment.id" 
                                       :href="attachment.download_url" 
                                       target="_blank"
                                       class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center gap-1">
                                        <i class="pi pi-download text-xs"></i>
                                        <span class="truncate max-w-[100px]" :title="attachment.original_filename">
                                            {{ attachment.original_filename }}
                                        </span>
                                    </a>
                                </div>
                            </div>
                            
                            <!-- Messages Container -->
                            <div class="bg-gray-50 rounded p-2 mb-2 h-[180px] overflow-y-auto flex-1" ref="chatContainer">
                                <div v-if="!subject.messages || subject.messages.length === 0" class="flex items-center justify-center h-full">
                                    <div class="text-center text-gray-500">
                                        <i class="pi pi-comments text-2xl mb-1"></i>
                                        <div class="text-sm">Belum ada pesan</div>
                                    </div>
                                </div>
                                <div v-else v-for="message in subject.messages" :key="message.id" class="mb-1">
                                    <div 
                                        class="flex items-start gap-1"
                                        :class="{
                                            'justify-end': message.dokumen_pengadaan_actor_id === dokumenPengadaan.current_actor_id,
                                        }"
                                    >
                                        <div 
                                            class="max-w-[85%] rounded p-1.5 text-xs"
                                            :class="{
                                                'bg-green-100': message.dokumen_pengadaan_actor_id === dokumenPengadaan.current_actor_id,
                                                'bg-blue-50': message.penyedia_id,
                                                'bg-yellow-50': message.actor?.role_id === 1,
                                                'bg-purple-50': message.actor?.role_id === 2,
                                                'bg-gray-100': !message.penyedia_id && !message.actor?.role_id
                                            }"
                                        >
                                            <div class="text-[10px] font-semibold text-gray-700 flex items-center gap-1">
                                                <span>{{ message.penyedia_id ? 'Penyedia' : (message.actor?.role?.name || 'Unknown') }}</span>
                                                <span v-if="message.dokumen_pengadaan_actor_id === dokumenPengadaan.current_actor_id" class="bg-green-200 text-green-800 px-1 py-0 rounded text-[9px]">Anda</span>
                                            </div>
                                            <div class="text-gray-800 break-words text-[11px]" v-html="formatMessage(message.message)"></div>
                                            <div class="text-[9px] text-gray-500 text-right">
                                                {{ formatDate(message.created_at) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Message Input -->
                            <div class="border-t pt-1 px-1">
                                <form @submit.prevent="sendMessage(subject.id)" class="flex items-end space-x-1">
                                    <div class="flex-1">
                                        <Textarea
                                            v-model="messageInputs[subject.id]"
                                            rows="1"
                                            class="w-full text-xs p-inputtext-sm"
                                            placeholder="Tulis pesan..."
                                            @keydown.enter.prevent="sendMessage(subject.id)"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        icon="pi pi-send"
                                        class="p-button-primary p-button-sm p-button-text"
                                        :loading="loading"
                                    />
                                </form>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-if="localSubjects.length === 0" class="text-center py-4">
                            <div class="text-gray-500">
                                <i class="pi pi-inbox text-4xl mb-2"></i>
                                <p class="text-base">Belum ada topik penjelasan</p>
                                <p class="text-xs">Mulai dengan membuat topik baru</p>
                            </div>
                        </div>
                    </div>

                    <!-- Create New Subject Button -->
                    <div class="mt-6" v-if="canCreateSubject">
                        <button @click="showCreateModal = true" 
                                class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">
                            Buat Pengumuman
                        </button>
                    </div>

                    <!-- Create Subject Modal -->
                    <Dialog 
                        v-model:visible="showCreateModal"
                        modal
                        header="Buat Pengumuman"
                        :style="{ width: '50vw' }">
                        <form @submit.prevent="submitNewSubject" enctype="multipart/form-data">
                            <div class="space-y-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700">Topik</label>
                                    <InputText
                                        v-model="form.subject"
                                        class="mt-1 block w-full"
                                        placeholder="Masukkan pengumuman"
                                        required
                                    />
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-gray-700">Deskripsi</label>
                                    <Textarea
                                        v-model="form.description"
                                        class="mt-1 block w-full"
                                        rows="4"
                                        placeholder="Masukkan deskripsi pengumuman"
                                        required
                                    />
                                </div>

                                <!-- File Upload Section -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Lampiran File (Opsional)</label>
                                    <div class="border-2 border-dashed border-gray-300 rounded-lg p-4">
                                        <input
                                            ref="fileInput"
                                            type="file"
                                            multiple
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                            @change="handleFileSelect"
                                            class="hidden"
                                        />
                                        
                                        <div class="text-center">
                                            <i class="pi pi-cloud-upload text-3xl text-gray-400 mb-2"></i>
                                            <p class="text-sm text-gray-600 mb-2">
                                                Pilih file atau drag & drop di sini
                                            </p>
                                            <Button
                                                type="button"
                                                label="Pilih File"
                                                icon="pi pi-upload"
                                                class="p-button-outlined p-button-sm"
                                                @click="$refs.fileInput.click()"
                                            />
                                            <p class="text-xs text-gray-500 mt-2">
                                                Maksimal 10MB per file. Format: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG
                                            </p>
                                        </div>
                                    </div>

                                    <!-- Selected Files List -->
                                    <div v-if="selectedFiles.length > 0" class="mt-3">
                                        <h4 class="text-sm font-medium text-gray-700 mb-2">File Terpilih:</h4>
                                        <div class="space-y-2">
                                            <div v-for="(file, index) in selectedFiles" :key="index" 
                                                 class="flex items-center justify-between p-2 bg-gray-50 rounded border">
                                                <div class="flex items-center space-x-2">
                                                    <i class="pi pi-file text-blue-500"></i>
                                                    <span class="text-sm text-gray-700">{{ file.name }}</span>
                                                    <span class="text-xs text-gray-500">({{ formatFileSize(file.size) }})</span>
                                                </div>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-times"
                                                    class="p-button-text p-button-sm p-button-danger"
                                                    @click="removeFile(index)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-6 flex justify-end gap-2">
                                <Button
                                    type="button"
                                    label="Batal"
                                    severity="secondary"
                                    @click="closeCreateModal"
                                />
                                <Button
                                    type="submit"
                                    label="Simpan"
                                    :loading="form.processing"
                                />
                            </div>
                        </form>
                    </Dialog>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import AppLayout from '@/Layouts/AppLayout.vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import axios from 'axios'
import dayjs from 'dayjs'

export default {
    components: {
        AppLayout,
        Dialog,
        Button,
        InputText,
        Textarea,
        Toast
    },

    props: {
        dokumenPengadaan: {
            type: Object,
            required: true
        },
        subjects: {
            type: Array,
            required: true
        },
        canCreateSubject: {
            type: Boolean,
            default: false
        }
    },

    setup() {
        return {
            toast: useToast()
        }
    },

    data() {
        return {
            showCreateModal: false,
            loading: false,
            messageInputs: {},
            refreshIntervals: {},
            subjectsRefreshInterval: null,
            localSubjects: [],
            visibleSubjects: new Set(), // Track which subjects are visible
            intersectionObserver: null,
            form: {
                subject: '',
                description: '',
                dokumen_pengadaan_id: this.dokumenPengadaan.id,
                processing: false
            },
            selectedFiles: []
        }
    },

    mounted() {
        // Initialize local subjects from props
        this.localSubjects = [...this.subjects];
        
        // Initialize message inputs for each subject
        this.localSubjects.forEach((subject) => {
            this.messageInputs[subject.id] = '';
        });

        // Set up intersection observer for visible subjects
        this.setupIntersectionObserver();
        
        // Set up refresh interval for subjects list
        this.setupSubjectsRefreshInterval();
        
        // Wait for DOM to be ready before observing elements
        this.$nextTick(() => {
            this.observeSubjects();
        });
    },

    beforeUnmount() {
        // Clear all intervals when component is unmounted
        Object.values(this.refreshIntervals).forEach(interval => {
            clearInterval(interval);
        });
        
        // Clear subjects refresh interval
        if (this.subjectsRefreshInterval) {
            clearInterval(this.subjectsRefreshInterval);
        }
        
        // Disconnect intersection observer
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    },

    updated() {
        // Re-observe subjects when the list updates
        this.$nextTick(() => {
            this.observeSubjects();
        });
    },

    methods: {
        setupIntersectionObserver() {
            // Create intersection observer to track visible subjects
            this.intersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const subjectId = parseInt(entry.target.dataset.subjectId);
                    
                    if (entry.isIntersecting) {
                        // Subject is visible, start refreshing its messages
                        this.visibleSubjects.add(subjectId);
                        this.setupRefreshInterval(subjectId);
                    } else {
                        // Subject is not visible, stop refreshing its messages
                        this.visibleSubjects.delete(subjectId);
                        this.clearRefreshInterval(subjectId);
                    }
                });
            }, {
                root: null, // Use viewport as root
                rootMargin: '0px',
                threshold: 0.1 // Trigger when 10% of the element is visible
            });
        },

        observeSubjects() {
            if (!this.intersectionObserver) return;
            
            // Disconnect existing observations
            this.intersectionObserver.disconnect();
            
            // Observe all subject elements
            const subjectElements = document.querySelectorAll('[data-subject-id]');
            subjectElements.forEach(element => {
                this.intersectionObserver.observe(element);
            });
        },

        setupSubjectsRefreshInterval() {
            // Clear existing interval if any
            if (this.subjectsRefreshInterval) {
                clearInterval(this.subjectsRefreshInterval);
            }
            
            // Set up a new interval to refresh subjects every 10 seconds
            this.subjectsRefreshInterval = setInterval(() => {
                this.refreshSubjects();
            }, 10000);
        },

        refreshSubjects() {
            axios.get(route('subject-penjelasan.api-index', {
                dokumenPengadaan: this.dokumenPengadaan.id
            })).then(response => {
                if (response.data.success) {
                    const newSubjects = response.data.subjects;
                    
                    // Check for new subjects
                    const existingSubjectIds = this.localSubjects.map(s => s.id);
                    const newSubjectItems = newSubjects.filter(s => !existingSubjectIds.includes(s.id));
                    
                    if (newSubjectItems.length > 0) {
                        // Add new subjects to the list
                        this.localSubjects = newSubjects;
                        
                        // Initialize message inputs for new subjects
                        newSubjectItems.forEach((subject) => {
                            this.messageInputs[subject.id] = '';
                        });
                        
                        // Show notification for new subjects
                        this.toast.add({
                            severity: 'info',
                            summary: 'Subjek Baru',
                            detail: `${newSubjectItems.length} subjek baru ditambahkan`,
                            life: 5000
                        });
                    } else {
                        // Update existing subjects with any changes (like new messages counts, etc.)
                        this.localSubjects = newSubjects;
                    }
                }
            }).catch(error => {
                console.error('Error refreshing subjects:', error);
            });
        },

        setupRefreshInterval(subjectId) {
            // Clear existing interval if any
            if (this.refreshIntervals[subjectId]) {
                clearInterval(this.refreshIntervals[subjectId]);
            }
            
            // Set up a new interval for messages refresh every 5 seconds (only for visible subjects)
            this.refreshIntervals[subjectId] = setInterval(() => {
                if (this.visibleSubjects.has(subjectId)) {
                    this.refreshSubjectMessages(subjectId);
                }
            }, 5000);
        },

        clearRefreshInterval(subjectId) {
            if (this.refreshIntervals[subjectId]) {
                clearInterval(this.refreshIntervals[subjectId]);
                delete this.refreshIntervals[subjectId];
            }
        },
        
        refreshSubjectMessages(subjectId) {
            // Only refresh if subject is still visible
            if (!this.visibleSubjects.has(subjectId)) {
                return;
            }
            
            axios.get(route('message-penjelasan.index', {
                dokumen_pengadaan_id: this.dokumenPengadaan.id,
                subject_penjelasan_id: subjectId
            })).then(response => {
                const subjectIndex = this.localSubjects.findIndex(s => s.id === subjectId);
                if (subjectIndex !== -1) {
                    // Check if there are new messages
                    const currentMessageCount = this.localSubjects[subjectIndex].messages?.length || 0;
                    const newMessageCount = response.data.length;
                    
                    this.localSubjects[subjectIndex].messages = response.data;
                    
                    // Show notification only if there are new messages
                    if (newMessageCount > currentMessageCount && currentMessageCount > 0) {
                        this.toast.add({
                            severity: 'success',
                            summary: 'Pesan Baru',
                            detail: `Pesan baru pada: ${this.localSubjects[subjectIndex].subject}`,
                            life: 4000
                        });
                    }
                }
            }).catch(error => {
                console.error('Error refreshing messages:', error);
            });
        },

        submitNewSubject() {
            this.form.processing = true;
            
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('subject', this.form.subject);
            formData.append('description', this.form.description);
            formData.append('dokumen_pengadaan_id', this.form.dokumen_pengadaan_id);
            
            // Add selected files to FormData
            if (this.$refs.fileInput && this.$refs.fileInput.files) {
                for (let i = 0; i < this.$refs.fileInput.files.length; i++) {
                    formData.append('attachments[]', this.$refs.fileInput.files[i]);
                }
            }
            
            // Use Inertia for form submission with file upload
            this.$inertia.post(route('subject-penjelasan.store'), formData, {
                preserveScroll: true,
                forceFormData: true,
                onSuccess: (page) => {
                    // Check for error message in the session flash data
                    if (page.props.flash && page.props.flash.error) {
                        this.toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: page.props.flash.error,
                            life: 5000
                        });
                        return;
                    }
                    
                    this.closeCreateModal();
                    this.toast.add({
                        severity: 'success',
                        summary: 'Sukses',
                        detail: page.props.flash && page.props.flash.success ? page.props.flash.success : 'Topik berhasil dibuat dengan lampiran',
                        life: 3000
                    });
                    
                    // Refresh subjects data
                    this.refreshSubjects();
                },
                onError: (errors) => {
                    console.error('Error creating subject:', errors);
                    let errorMessage = 'Gagal membuat topik';
                    if (errors.subject) {
                        errorMessage += ': ' + errors.subject[0];
                    } else if (errors.description) {
                        errorMessage += ': ' + errors.description[0];
                    } else if (errors.attachments) {
                        errorMessage += ': ' + errors.attachments[0];
                    }
                    
                    this.toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMessage,
                        life: 3000
                    });
                },
                onFinish: () => {
                    this.form.processing = false;
                }
            });
        },

        sendMessage(subjectId) {
            if (!this.messageInputs[subjectId]?.trim() || this.loading) return;
            
            this.loading = true;
            
            axios.post(route('message-penjelasan.store'), {
                dokumen_pengadaan_id: this.dokumenPengadaan.id,
                subject_penjelasan_id: subjectId,
                message: this.messageInputs[subjectId]
            }).then((response) => {
                this.messageInputs[subjectId] = '';
                
                // Add the new message to the subject's messages
                const subjectIndex = this.localSubjects.findIndex(s => s.id === subjectId);
                if (subjectIndex !== -1) {
                    // Initialize messages array if it doesn't exist
                    if (!this.localSubjects[subjectIndex].messages) {
                        this.localSubjects[subjectIndex].messages = [];
                    }
                    
                    const newMessage = response.data;
                    // No need to set display_name here, we'll use actor.role.name directly in the template
                    this.localSubjects[subjectIndex].messages.push(newMessage);
                }
                
                this.toast.add({
                    severity: 'success',
                    summary: 'Sukses',
                    detail: 'Pesan terkirim',
                    life: 3000
                });
            }).catch((error) => {
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal mengirim pesan: ' + (error.response?.data?.message || error.message),
                    life: 3000
                });
            }).finally(() => {
                this.loading = false;
            });
        },

        refreshMessages() {
            // This method is no longer used - we now refresh each subject individually
        },

        formatDate(date) {
            return dayjs(date).format('DD/MM/YYYY HH:mm');
        },
        
        decodeHtml(html) {
            if (!html) return '';
            
            // Create a temporary element to decode HTML entities
            const textarea = document.createElement('textarea');
            textarea.innerHTML = html;
            return textarea.value;
        },
        
        formatMessage(message) {
            // Convert plain text URLs to clickable links
            if (!message) return '';
            
            // First decode HTML entities
            const decodedMessage = this.decodeHtml(message);
            
            // Simple URL regex
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            return decodedMessage.replace(urlRegex, url => `<a href="${url}" target="_blank" class="text-blue-600 hover:underline">${url}</a>`);
        },

        handleFileSelect(event) {
            const files = event.target.files;
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.selectedFiles.push({
                    name: file.name,
                    size: file.size
                });
            }
        },

        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },

        closeCreateModal() {
            this.showCreateModal = false;
            this.form.subject = '';
            this.form.description = '';
            this.selectedFiles = [];
            
            // Reset file input
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        formatFileSize(size) {
            if (size < 1024) {
                return size + ' B';
            } else if (size < 1024 * 1024) {
                return (size / 1024).toFixed(2) + ' KB';
            } else if (size < 1024 * 1024 * 1024) {
                return (size / (1024 * 1024)).toFixed(2) + ' MB';
            } else {
                return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
            }
        }
    }
}
</script>

<style scoped>
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 50;
}

.loading-text {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #4b5563;
}
</style>
